/* Widget UI strings. The bot's reply language comes from the server prompt;
   this is the chrome around it — and a Spanish "Hablar con persona" button on
   an English site is exactly as wrong as a Spanish answer.

   Language comes from the product's `chatbotLanguage` column via the public
   config endpoint, so a bot is one language end to end. */

export type WidgetLanguage = 'es' | 'en'

export interface WidgetStrings {
  bubbleLabel: string
  openChat: string
  closeChat: string
  dialogLabel: string
  online: string
  talkToPerson: string
  talkToPersonTitle: string
  typing: string
  inputPlaceholder: string
  send: string
  reset: string
  resetConfirm: string
  welcomeFallback: string
  genericError: string
  escalateError: string
  escalateIntro: string
  whatsappCta: string
  whatsappMessage: (brand: string) => string
  whatsappOutsideHours: string
  whatsappRateLimited: string
  email: string
  emailPlaceholder: string
  nameOptional: string
  namePlaceholder: string
  messageOptional: string
  messagePlaceholder: string
  cancel: string
  submit: string
  submitting: string
  successTitle: string
  successBody: string
  defaultBrand: string
}

const es: WidgetStrings = {
  bubbleLabel: 'Soporte',
  openChat: 'Abrir chat',
  closeChat: 'Cerrar chat',
  dialogLabel: 'Chat de soporte',
  online: 'En línea',
  talkToPerson: 'Hablar con persona',
  talkToPersonTitle: 'Hablar con una persona',
  typing: 'Escribiendo',
  inputPlaceholder: 'Escribí tu pregunta…',
  send: 'Enviar',
  reset: 'Borrar conversación',
  resetConfirm: '¿Borrar esta conversación?',
  welcomeFallback: '¡Hola! ¿En qué te puedo ayudar hoy?',
  genericError: 'No pude responder en este momento. Tocá "Hablar con persona" arriba y el equipo te ayuda.',
  escalateError: 'No pudimos enviar el mensaje. Probá de nuevo.',
  escalateIntro: 'Dejanos tu email y te respondemos pronto.',
  whatsappCta: 'Enviar mensaje por WhatsApp',
  whatsappMessage: brand => `Hola ${brand}, necesito hablar con una persona.`,
  whatsappOutsideHours: 'Estamos fuera de horario de atención. Dejanos tu email abajo y te respondemos pronto.',
  whatsappRateLimited: 'Ya enviaste varios mensajes por acá. Dejanos tu email abajo y te respondemos pronto.',
  email: 'Email',
  emailPlaceholder: 'tu@email.com',
  nameOptional: 'Nombre (opcional)',
  namePlaceholder: 'Tu nombre',
  messageOptional: 'Mensaje (opcional)',
  messagePlaceholder: 'Contanos brevemente en qué te podemos ayudar…',
  cancel: 'Cancelar',
  submit: 'Enviar',
  submitting: 'Enviando…',
  successTitle: '¡Gracias!',
  successBody: 'Te respondemos en las próximas horas.',
  defaultBrand: 'el equipo',
}

const en: WidgetStrings = {
  bubbleLabel: 'Support',
  openChat: 'Open chat',
  closeChat: 'Close chat',
  dialogLabel: 'Support chat',
  online: 'Online',
  talkToPerson: 'Talk to a person',
  talkToPersonTitle: 'Talk to a person',
  typing: 'Typing',
  inputPlaceholder: 'Ask your question…',
  send: 'Send',
  reset: 'Clear conversation',
  resetConfirm: 'Clear this conversation?',
  welcomeFallback: 'Hi! How can I help you today?',
  genericError: 'I couldn\'t answer just now. Tap "Talk to a person" above and the team will help.',
  escalateError: 'We couldn\'t send your message. Please try again.',
  escalateIntro: 'Leave your email and we\'ll get back to you shortly.',
  whatsappCta: 'Send a WhatsApp message',
  whatsappMessage: brand => `Hi ${brand}, I'd like to speak to a person.`,
  whatsappOutsideHours: 'We\'re outside business hours right now. Leave your email below and we\'ll get back to you soon.',
  whatsappRateLimited: 'You\'ve sent several messages this way already. Leave your email below and we\'ll get back to you soon.',
  email: 'Email',
  emailPlaceholder: 'you@email.com',
  nameOptional: 'Name (optional)',
  namePlaceholder: 'Your name',
  messageOptional: 'Message (optional)',
  messagePlaceholder: 'Tell us briefly what you need help with…',
  cancel: 'Cancel',
  submit: 'Send',
  submitting: 'Sending…',
  successTitle: 'Thank you!',
  successBody: 'We\'ll get back to you within a few hours.',
  defaultBrand: 'the team',
}

const STRINGS: Record<WidgetLanguage, WidgetStrings> = { es, en }

export function toWidgetLanguage(value: string | null | undefined): WidgetLanguage {
  return value === 'en' ? 'en' : 'es'
}

export function widgetStrings(language: string | null | undefined): WidgetStrings {
  return STRINGS[toWidgetLanguage(language)]
}
